import { useState } from "react";
import PageTransition from "./Pagetransition";
import Hero from "./Hero";
import { RevealBox, SectionLabel, SectionTitle, Divider } from "./Sectioncomponents";
import { useCounter, useReveal } from "./useReveal";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import ProductosPage from "./Productospage";
import UbicacionPage from "./Ubicacionpage";
import ContactoPage from "./Contactpage";
import monaco2 from "../assets/monaco2.png";

function StatCounter({ target, label }) {
  const { ref, inView } = useReveal();
  const value = useCounter(target, inView, 1200);
  return (
    <div ref={ref} className="text-left">
      <div
        className="font-serif text-[3.5rem] font-semibold leading-none bg-gradient-to-br from-[#f0c96b] to-[#c9973a] bg-clip-text text-transparent"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {value}
      </div>
      <div
        className="mt-[0.3rem] text-[0.7rem] uppercase tracking-[0.2em] text-[#6b6257]"
        style={{ fontFamily: "'Jost', sans-serif" }}
      >
        {label}
      </div>
    </div>
  );
}

function MonacoImage({ onZoom }) {
  return (
    <div className="relative h-full w-full overflow-hidden">
      <img
        src={monaco2}
        alt="Monaco Aberturas - Local"
        onClick={onZoom}
        className="h-full w-full cursor-zoom-in object-cover transition-transform duration-700 hover:scale-105"
      />

      {/* overlay sutil para mantener la estética dorada */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1a1814]/70 via-transparent to-[#1a1814]/10" />

      {/* borde dorado interno */}
      <div className="pointer-events-none absolute inset-0 border border-[rgba(201,151,58,0.25)]" />

      {/* etiqueta */}
      <div className="pointer-events-none absolute bottom-5 left-0 right-0 text-center">
        <span
          className="text-[0.85rem] italic tracking-[0.3em] text-[#f0c96b]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Nuestro Showroom
        </span>
      </div>

      {/* hint de zoom */}
      <div
        className="
          pointer-events-none
          absolute
          right-4
          top-4
          flex
          h-9
          w-9
          items-center
          justify-center
          rounded-full
          border
          border-[rgba(201,151,58,0.4)]
          bg-[#1a1814]/60
          text-[#f0c96b]
          backdrop-blur-sm
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          style={{ width: 16, height: 16 }}
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="11" y1="8" x2="11" y2="14" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
      </div>
    </div>
  );
}

/* Lightbox para ver la imagen del local en grande, con zoom táctil/click */
function ImageLightbox({ src, onClose }) {
  const [zoomed, setZoomed] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      className="
        fixed
        inset-0
        z-[999]
        flex
        items-center
        justify-center
        bg-[#0f0d0b]/95
        p-4
        backdrop-blur-md
      "
    >
      {/* botón cerrar */}
      <button
        onClick={onClose}
        aria-label="Cerrar"
        className="
          absolute
          right-5
          top-5
          flex
          h-11
          w-11
          items-center
          justify-center
          rounded-full
          border
          border-[rgba(201,151,58,0.4)]
          bg-[#1a1814]/70
          text-[#f0c96b]
        "
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          style={{ width: 18, height: 18 }}
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <motion.img
        src={src}
        alt="Monaco Aberturas - Local (vista ampliada)"
        onClick={(e) => {
          e.stopPropagation();
          setZoomed((z) => !z);
        }}
        animate={{ scale: zoomed ? 1.9 : 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="
          max-h-[88vh]
          max-w-[92vw]
          cursor-zoom-in
          select-none
          border
          border-[rgba(201,151,58,0.25)]
          object-contain
        "
      />

      <span
        className="
          absolute
          bottom-6
          left-1/2
          -translate-x-1/2
          text-[0.7rem]
          uppercase
          tracking-[0.3em]
          text-[#b7aea2]
        "
        style={{ fontFamily: "'Jost', sans-serif" }}
      >
        Tocá la imagen para hacer zoom
      </span>
    </motion.div>
  );
}

/* Lista de líneas de productos + vidrios + showroom, en el mismo registro
   visual que el resto de la sección (Jost, dorado, minúscula puntuación). */
function InfoList() {
  const items = [
    {
      label: "Líneas de aberturas",
      detail: "Herrero, Modena, puente de ruptura térmica y PVC: excelencia en calidad y durabilidad.",
    },
    {
      label: "Vidrios y espejos",
      detail: "Vidrios laminados, DVH (doble vidrio hermético) y espejos de diseño.",
    },
    {
      label: "Showroom",
      detail: "Saint James 998. Atención personalizada y garantía de calidad en cada proyecto.",
    },
  ];

  return (
    <ul className="mb-12 flex flex-col gap-5">
      {items.map((item) => (
        <li key={item.label} className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="mt-[0.45rem] h-[6px] w-[6px] flex-shrink-0 rounded-full bg-gradient-to-br from-[#f0c96b] to-[#c9973a]"
          />
          <p
            className="text-[1rem] font-light leading-[1.8] text-[rgba(249,245,239,0.8)]"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            <span className="text-[#e9c97f]">{item.label}.</span>{" "}
            {item.detail}
          </p>
        </li>
      ))}
    </ul>
  );
}

export default function HomePage() {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <PageTransition>
      <Hero />

      <section className="relative overflow-hidden bg-gradient-to-b from-[#1a1814] to-[#151210] py-28">
        <span
          className="pointer-events-none absolute right-[-2rem] top-1/2 -translate-y-1/2 rotate-90 select-none whitespace-nowrap text-[12rem] font-bold text-[rgba(201,151,58,0.03)] tracking-[0.2em]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          MONACO
        </span>

        <div className="mx-auto max-w-[1200px] px-8">
          <div className="grid grid-cols-1 items-center gap-20 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
            <div>
              <RevealBox><SectionLabel>Nuestra Historia</SectionLabel></RevealBox>
              <RevealBox delay={0.1}>
                <SectionTitle>
                  Calidad que <em>transforma</em><br />cada espacio
                </SectionTitle>
              </RevealBox>
              <RevealBox delay={0.2}><Divider /></RevealBox>
              <RevealBox delay={0.25}>
                <p
                  className="mb-5 text-[1.05rem] font-light leading-[1.9] text-[rgba(249,245,239,0.75)]"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  En Monaco Aberturas nos especializamos en la fabricación e instalación de aberturas de aluminio de alta gama. Cada proyecto tratado con el mismo compromiso de excelencia y atención al detalle que nos define.
                </p>
                <p
                  className="mb-10 text-[1.05rem] font-light leading-[1.9] text-[rgba(249,245,239,0.75)]"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  Trabajamos con los mejores perfiles del mercado para garantizar durabilidad, hermeticidad y una estética impecable que eleva el valor de cualquier propiedad.
                </p>
              </RevealBox>

              <RevealBox delay={0.3}>
                <InfoList />
              </RevealBox>

              <div className="flex flex-wrap gap-12">
                <StatCounter target={10} label="+ Años de experiencia" />
                <StatCounter target={500} label="+ Proyectos realizados" />
                <StatCounter target={100} label="% Garantía de calidad" />
              </div>

              <div className="mt-10">
                <Link to="/productos" className="no-underline">
                  <motion.span
                    whileHover={{ y: -2, boxShadow: "0 12px 40px rgba(201,151,58,0.4)" }}
                    whileTap={{ scale: 0.97 }}
                    className="inline-block cursor-none bg-gradient-to-br from-[#8b6220] via-[#c9973a] to-[#f0c96b] px-10 py-4 text-[0.8rem] font-semibold uppercase tracking-[0.25em] text-[#1a1814]"
                    style={{ fontFamily: "'Jost', sans-serif" }}
                  >
                    Ver productos
                  </motion.span>
                </Link>
              </div>
            </div>

            <RevealBox delay={0.15}>
              <div className="relative overflow-hidden border border-[rgba(201,151,58,0.15)] bg-gradient-to-br from-[#1e1a14] to-[#252015] p-5 md:p-6">
                <div className="absolute inset-x-0 top-0 z-10 h-[2px] bg-gradient-to-r from-transparent via-[#c9973a] to-transparent" />
                <MonacoImage onZoom={() => setLightboxOpen(true)} />
              </div>
            </RevealBox>
          </div>
        </div>
      </section>
      <ProductosPage />
      <UbicacionPage />
      <ContactoPage />

      <AnimatePresence>
        {lightboxOpen && (
          <ImageLightbox
            src={monaco2}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </AnimatePresence>
    </PageTransition>
  );
}